import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tabs, Tag, Slider } from 'antd';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { useSelector } from 'react-redux';
import {
  getInventoryReportApi, getEmployeeHistoryReportApi,
  getLifecycleReportApi, getVarianceReportApi,
  getLowStockApi, getDueReplacementsApi,
  exportInventoryReportApi, exportVarianceReportApi, exportDueReplacementsApi,
  exportLifecycleReportApi,
} from 'api/services/uniformManagement';
import { useUniformMgmt } from '../context/uniformMgmtContext';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../constants';
import Breadcrumbs from 'components/Breadcrumbs';
import ExcelExportButton from 'components/ExcelExportButton';

const ReportsDashboard = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();
  const { userData, userDataLoading } = useSelector((state) => state.common);
  const [activeTab, setActiveTab] = useState('inventory');

  const employeeOptions = (userData || [])
    .slice()
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
    .map(u => ({
      label: `${u.first_name} ${u.last_name}${u.emp_no ? ` (${u.emp_no})` : ''}`,
      value: u.id, emp_no: u.emp_no || ''
    }));

  const tabItems = [
    { key: 'inventory', label: 'Inventory', children: <InventoryReport warehouseOptions={warehouseOptions} warehousesLoading={warehousesLoading} /> },
    { key: 'employee', label: 'Employee History', children: <EmployeeHistoryReport employeeOptions={employeeOptions} userDataLoading={userDataLoading} navigate={navigate} /> },
    { key: 'lifecycle', label: 'Lifecycle / Replacements', children: <LifecycleReport /> },
    { key: 'variance', label: 'Variance', children: <VarianceReport warehouseOptions={warehouseOptions} warehousesLoading={warehousesLoading} /> },
    { key: 'lowstock', label: 'Low Stock', children: <LowStockWidget /> },
    { key: 'due', label: 'Due Replacements', children: <DueReplacementsWidget navigate={navigate} /> },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Uniform Reports" />
<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} type="card" />
      </div>
    </div>
  );
};

// ==================== Inventory Report ====================
const InventoryReport = ({ warehouseOptions, warehousesLoading }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterWarehouse, setFilterWarehouse] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterWarehouse) params.warehouse = filterWarehouse.value;
    getInventoryReportApi(params).then(response => {
      setData(response.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filterWarehouse]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    {
      title: 'Quantity', dataIndex: 'quantity',
      render: (qty, record) => {
        const isLow = record.reorder_level > 0 && qty <= record.reorder_level;
        return <span style={{ color: isLow ? '#ff4d4f' : 'inherit', fontWeight: isLow ? 600 : 400 }}>{qty}</span>;
      }
    },
    { title: 'Reorder Level', dataIndex: 'reorder_level', render: (val) => val || '-' }
  ];

  return (
    <div>
      <div className="row mb-3 align-items-center">
        <div className="col-md-3">
          <FilterSelect isClearable onChange={setFilterWarehouse} optionLabel="label" optionValue="value"
            options={warehouseOptions} value={filterWarehouse} placeholder="Filter by Warehouse" isLoading={warehousesLoading} />
        </div>
        <div className="col-md-9 text-end">
          <ExcelExportButton
            apiFn={exportInventoryReportApi}
            params={filterWarehouse ? { warehouse: filterWarehouse.value } : {}}
            filename="Uniform Inventory.xlsx"
          />
        </div>
      </div>
      <Table columns={columns} dataSource={data} rowKey={(r, i) => i} loading={loading}
        pagination={{ showSizeChanger: true }} scroll={{ x: "max-content" }} />
    </div>
  );
};

// ==================== Employee History Report ====================
const EmployeeHistoryReport = ({ employeeOptions, userDataLoading, navigate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    if (selectedEmployee) {
      setLoading(true);
      getEmployeeHistoryReportApi(selectedEmployee.value).then(response => {
        setData(response.data?.data || []);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      setData([]);
    }
  }, [selectedEmployee]);

  const columns = [
    { title: 'Uniform', dataIndex: 'uniform_item' },
    { title: 'Size', dataIndex: 'size' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Warehouse', dataIndex: 'warehouse' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Status', dataIndex: 'status', render: (s) => <span style={getStatusBadge(s)}>{s}</span> },
    { title: 'Next Replacement', dataIndex: 'next_replacement_date', render: (d) => d ? formatDateHandler(d) : '-' },
    { title: 'Issued By', dataIndex: 'issued_by' }
  ];

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-4">
          <FilterSelect isClearable onChange={setSelectedEmployee} optionLabel="label" optionValue="value"
            options={employeeOptions} value={selectedEmployee} placeholder="Select Employee" isLoading={userDataLoading} />
        </div>
        {selectedEmployee && (
          <div className="col-md-2">
            <Button label="View Card" icon="fa fa-id-card" className="btn btn-outline-primary btn-sm"
              onClick={() => navigate(`/uniform-management/employee-card/${selectedEmployee.value}`)} />
          </div>
        )}
      </div>
      {selectedEmployee ? (
        <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
          pagination={{ showSizeChanger: true }} scroll={{ x: "max-content" }} />
      ) : (
        <div className="text-center py-5 text-muted">Select an employee to view their issuance history</div>
      )}
    </div>
  );
};

// ==================== Lifecycle Report ====================
const LifecycleReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  // `daysAhead` updates instantly while dragging (UI label).
  // `committedDays` only updates when the user releases the slider — that's
  // what actually triggers the API call, so we don't fire 100s of requests.
  const [daysAhead, setDaysAhead] = useState(90);
  const [committedDays, setCommittedDays] = useState(90);

  const fetchData = useCallback(() => {
    setLoading(true);
    getLifecycleReportApi({ days_ahead: committedDays }).then(response => {
      setData(response.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [committedDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { title: 'Employee', dataIndex: 'employee_name' },
    { title: 'Emp No', dataIndex: 'emp_no' },
    { title: 'Uniform', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Replacement Date', dataIndex: 'next_replacement_date', render: (d) => formatDateHandler(d) },
    {
      title: 'Days Until', dataIndex: 'days_until_replacement',
      render: (days) => (
        <span style={{ color: days < 0 ? '#ff4d4f' : days <= 14 ? '#faad14' : '#52c41a', fontWeight: 600 }}>
          {days < 0 ? `${Math.abs(days)} overdue` : `${days} days`}
        </span>
      )
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <Tag color={status === 'overdue' ? 'red' : 'orange'}>{status}</Tag>
    }
  ];

  const overdueCount = data.filter(d => d.status === 'overdue').length;
  const upcomingCount = data.filter(d => d.status === 'upcoming').length;

  const presets = [30, 60, 90, 180, 365];

  const StatCard = ({ label, value, color, icon }) => (
    <div className="p-3 d-flex align-items-center gap-3" style={{
      background: '#fff', border: '1px solid #F1F2F4', borderRadius: 12, minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}15`, color: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
      }}>
        <i className={icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1F1F1F', lineHeight: 1.2 }}>
          {loading ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }} /> : value}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <StatCard label="Total in window" value={data.length} color="#2563EB" icon="fa fa-list" />
        </div>
        <div className="col-md-4">
          <StatCard label="Overdue" value={overdueCount} color="#DC2626" icon="fa fa-exclamation-triangle" />
        </div>
        <div className="col-md-4">
          <StatCard label="Upcoming" value={upcomingCount} color="#F59E0B" icon="fa fa-clock" />
        </div>
      </div>

      <div className="d-flex justify-content-end mb-2">
        <ExcelExportButton
          apiFn={exportLifecycleReportApi}
          params={{ days_ahead: committedDays }}
          filename="Uniform Lifecycle.xlsx"
        />
      </div>

      <div className="p-3 mb-3" style={{
        background: '#fff', border: '1px solid #F1F2F4', borderRadius: 12,
      }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div>
            <div className="fw-semibold" style={{ fontSize: 14 }}>Look-ahead window</div>
            <small className="text-muted">
              Show replacements due within the next <strong>{daysAhead}</strong> day{daysAhead === 1 ? '' : 's'}
            </small>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {presets.map(p => (
              <button
                key={p}
                type="button"
                className={`btn btn-sm ${committedDays === p ? 'btn-primary' : 'btn-light'}`}
                onClick={() => { setDaysAhead(p); setCommittedDays(p); }}
              >
                {p === 365 ? '1y' : `${p}d`}
              </button>
            ))}
          </div>
        </div>
        <Slider
          min={7}
          max={365}
          value={daysAhead}
          onChange={setDaysAhead}
          onChangeComplete={setCommittedDays}
          tooltip={{ formatter: (v) => `${v} days` }}
        />
      </div>

      <Table columns={columns} dataSource={data} rowKey={(r, i) => i} loading={loading}
        pagination={{ showSizeChanger: true }} scroll={{ x: "max-content" }} />
    </div>
  );
};

// ==================== Variance Report ====================
const VarianceReport = ({ warehouseOptions, warehousesLoading }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterWarehouse, setFilterWarehouse] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterWarehouse) params.warehouse = filterWarehouse.value;
    getVarianceReportApi(params).then(response => {
      setData(response.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filterWarehouse]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { title: 'Stock Take', dataIndex: 'stock_take_id' },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Date', dataIndex: 'conducted_date', render: (d) => formatDateHandler(d) },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    { title: 'System Qty', dataIndex: 'system_quantity' },
    { title: 'Physical Qty', dataIndex: 'physical_quantity' },
    {
      title: 'Variance', dataIndex: 'variance',
      render: (val) => (
        <span style={{ color: val < 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
          {val > 0 ? `+${val}` : val}
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="row mb-3 align-items-center">
        <div className="col-md-3">
          <FilterSelect isClearable onChange={setFilterWarehouse} optionLabel="label" optionValue="value"
            options={warehouseOptions} value={filterWarehouse} placeholder="Filter by Warehouse" isLoading={warehousesLoading} />
        </div>
        <div className="col-md-9 text-end">
          <ExcelExportButton
            apiFn={exportVarianceReportApi}
            params={filterWarehouse ? { warehouse: filterWarehouse.value } : {}}
            filename="Uniform Variance.xlsx"
          />
        </div>
      </div>
      <Table columns={columns} dataSource={data} rowKey={(r, i) => i} loading={loading}
        pagination={{ showSizeChanger: true }} scroll={{ x: "max-content" }} />
    </div>
  );
};

// ==================== Low Stock Widget ====================
const LowStockWidget = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getLowStockApi({}).then(response => {
      setData(response.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_name', render: (name, record) => `${name} (${record.size_code})` },
    {
      title: 'Quantity', dataIndex: 'quantity',
      render: (qty) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{qty}</span>
    },
    { title: 'Reorder Level', dataIndex: 'reorder_level' }
  ];

  return (
    <div>
      {data.length > 0 ? (
        <div className="alert alert-danger mb-3">
          <i className="fa fa-exclamation-triangle me-2" />
          {data.length} item(s) at or below reorder level
        </div>
      ) : (
        <div className="alert alert-success mb-3"><i className="fa fa-check me-2" />All stock levels are healthy</div>
      )}
      <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
        pagination={false} scroll={{ x: "max-content" }} />
    </div>
  );
};

// ==================== Due Replacements Widget ====================
const DueReplacementsWidget = ({ navigate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getDueReplacementsApi({ days_ahead: 30 }).then(response => {
      setData(response.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Employee', dataIndex: 'company_user_name' },
    { title: 'Emp No', dataIndex: 'company_user_emp_no' },
    { title: 'Uniform', dataIndex: 'uniform_item_name' },
    { title: 'Size', render: (_, r) => r.size_detail?.code || '-' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Replacement Due', dataIndex: 'next_replacement_date', render: (d) => formatDateHandler(d) },
    {
      title: 'Action',
      render: (_, record) => (
        <Button label="Re-issue" className="btn btn-sm btn-primary"
          onClick={() => navigate('/uniform-management/issuance')} />
      )
    }
  ];

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <ExcelExportButton
          apiFn={exportDueReplacementsApi}
          params={{ days_ahead: 30 }}
          filename="Uniform Due Replacements.xlsx"
        />
      </div>
      {data.length > 0 ? (
        <div className="alert alert-warning mb-3">
          <i className="fa fa-clock-o me-2" />
          {data.length} uniform(s) due for replacement in the next 30 days
        </div>
      ) : (
        <div className="alert alert-success mb-3"><i className="fa fa-check me-2" />No replacements due in the next 30 days</div>
      )}
      <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
        pagination={false} scroll={{ x: "max-content" }} />
    </div>
  );
};

export default ReportsDashboard;
